import { officeReadyPromise } from "../init";
import axios, { AxiosResponse } from "axios";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import * as ce from "./custom_errors";
import { getSetting } from "./setttings";
import { documentNamePrefix } from "./word_utils";
import { getCurrentLanguage } from "../locales/i18n";
import { SSO_TOKEN_NAME } from "./sso_utils";

document.addEventListener("DOMContentLoaded", async () => {
  try {
    await officeReadyPromise;
    updateApiUrl();
  } catch (error) {
    console.error("Fehler bei der Initialisierung:", error);
  }
});

/* eslint-disable-next-line no-undef */
export const defaultApiUrl = process.env.API_URL!;
export let apiUrl = defaultApiUrl;
export async function updateApiUrl() {
  apiUrl = (await getSetting("apiUrl")) ?? defaultApiUrl;
}

/* eslint-disable-next-line no-undef */
const llmUrl = process.env.LLM_URL;

export const defaultModel: string = "mistral-small3.2";

export class Models {
  private static instance: Models;
  private llmModelsDetailed: Promise<LlmModelDetailed[]>;

  private constructor() {
    this.llmModelsDetailed = getLlmModelsDetailed();
  }

  public static getInstance(): Models {
    if (!Models.instance) {
      Models.instance = new Models();
    }
    return Models.instance;
  }

  public async getAllLlmModelsDetailed(): Promise<LlmModelDetailed[]> {
    return this.llmModelsDetailed;
  }
}

export function getModels(): Models {
  return Models.getInstance();
}

export class Workflows {
  private static instance: Workflows;
  private workflows: Promise<Workflow[]>;

  private constructor() {
    this.workflows = getLlmWorkflows();
  }

  public static getInstance(): Workflows {
    if (!Workflows.instance) {
      Workflows.instance = new Workflows();
    }
    return Workflows.instance;
  }

  public async getWorkflows(): Promise<Workflow[]> {
    return this.workflows;
  }
}

export function getWorkflows(): Workflows {
  return Workflows.getInstance();
}

/**
 * This function logs in the user.
 *
 * @param username The jassper username
 * @param password The jassper password
 * @returns {Promise<void>} A promise that resolves when the login request is sent
 */
export async function login(username: string, password: string): Promise<void> {
  try {
    localStorage.removeItem("authToken");
    localStorage.removeItem("username");

    const response: AxiosResponse = await axios.post(`${apiUrl}/authenticate`, {
      username: username,
      password: password,
    });

    if (response.status === 200 || response.status === 201) {
      console.log("Login successful! Setting auth-token.");
      localStorage.setItem("authToken", response.data);
      const user = await getUserProfile();
      localStorage.setItem("username", user.username);

      return Promise.resolve();
    } else {
      console.error("Login failed! Status code: " + response.status);
      return Promise.reject(new Error("Login failed! Status code: " + response.status));
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        return Promise.reject(error.response);
      } else if (error.request) {
        return Promise.reject(new Error("Error trying to fetch auth-token: No response received"));
      }
    }
    return Promise.reject(new Error("Unexpected error trying to fetch auth-token"));
  }
}

export async function refreshSSOToken(refreshToken: string) {
  try {
    localStorage.removeItem(SSO_TOKEN_NAME);
    const response: AxiosResponse = await axios.post(`${apiUrl}/sso/refresh/token`, {
      refresh_token: refreshToken,
    });

    if (response.status === 200 || response.status === 201) {
      console.log("SSO token refresh successful");
      return response.data;
    } else {
      return Promise.reject(new Error("SSO token refresh failed! Status code: " + response.status));
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        return Promise.reject(error.response);
      } else if (error.request) {
        return Promise.reject(new Error("Error trying to refresh SSO token: No response received"));
      }
    }
    return Promise.reject(new Error("Unexpected error trying to refresh SSO token"));
  }
}

export async function getUserProfile(): Promise<{
  id: number;
  username: string;
  lastLogin: number;
  tenant: number;
  role: string;
}> {
  try {
    console.log("Getting user profile.");
    const response: AxiosResponse = await axios.get<{
      id: number;
      username: string;
      lastLogin: number;
      tenant: number;
      role: string;
    }>(`${apiUrl}/user/me`);
    if (200 <= response.status && response.status <= 201) {
      return response.data;
    }
    return Promise.reject();
  } catch (error) {
    console.log("Auth token is invalid.");
    return Promise.reject();
  }
}

export async function isAuthTokenValid(): Promise<boolean> {
  try {
    console.log("Checking if auth token is valid.");
    const response: AxiosResponse = await axios.get(`${apiUrl}/user/me`);
    return 200 <= response.status && response.status <= 201;
  } catch (error) {
    console.log("Auth token is invalid.");
    return false;
  }
}

axios.interceptors.request.use(
  (config: any) => {
    // ... create name variable for normal login auth token
    const token = localStorage.getItem("authToken") ?? localStorage.getItem(SSO_TOKEN_NAME);
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response: any) => {
    return response;
  },
  (error: any) => {
    if (error.response.status === 403) {
      if (localStorage.getItem("authToken")) {
        console.log("Token expired. Logging out.");
        localStorage.removeItem("authToken");
        document.location.href = "login.html";
      }
    }
    return Promise.reject(error);
  }
);

// #############################################################################
// ############################# LLM API FUNCTIONS #############################
// #############################################################################

export interface documentFile {
  name: string;
  originFileObj: File;
}
export interface V2CreateRawResponse {
  case_id: string;
}
export interface V2CreateResponse extends V2CreateRawResponse {
  text: string;
  isPDF?: boolean;
}

export async function v2CreateChatWithPlaybook(playbookId: string): Promise<V2CreateRawResponse> {
  try {
    const requestUrl = `${llmUrl}/v2/chat/playbook/${playbookId}`;
    const response = await axios.post(requestUrl);
    return response.data as V2CreateRawResponse;
  } catch (error) {
    console.error("Error trying to create chat with playbook: ", error);
    throw new ce.CaseCreationError("Could not create chat with playbook. Please try again later.");
  }
}
export async function v2CreateChatWithFiles(files: documentFile[]): Promise<V2CreateResponse> {
  try {
    const requestUrl = `${llmUrl}/v2/chat/document`;

    const formData = new FormData();
    for (const file of files) {
      formData.append("file", file.originFileObj, file.name);
    }

    const response = await axios.post(requestUrl, formData);

    return response.data as V2CreateResponse;
  } catch (error) {
    console.error("Error trying to create chat with document: ", error);
    throw new ce.CaseCreationError("Could not create chat with document. Please try again later.");
  }
}
export async function v2CreateChat(): Promise<V2CreateRawResponse> {
  try {
    const requestUrl = `${llmUrl}/v2/chat`;
    const response = await axios.post(requestUrl);

    return response.data as V2CreateResponse;
  } catch (error) {
    console.error("Error trying to create chat:", error);
    throw new ce.CaseCreationError("Could not create chat. Please try again later.");
  }
}

export type LLMV2Type = "chat" | "rag" | "playbook" | "complete_document";
export interface LLMStreamV2body {
  prompt: string;
  model: string;
  type: LLMV2Type; // 'rag', 'playbook' or 'chat'
  system_prompt?: string;
  context_window?: number;
}
export interface v2CaseCreatedResponse {
  content: string;
  type: string;
}
export interface LLMStreamV2Params {
  endpointPath: string;
  model: string;
  llmStreamV2body?: LLMStreamV2body;
  llmStreamV2formData?: FormData;
  otherData?: any;
  cancelStream?: () => void;
  onQuestion?: (data: IOMainLLMMessage) => Promise<void>;
  onAnswer?: (data: IOMainLLMMessage) => Promise<void>;
  onFollowupQuestion?: (data: IOMainLLMMessage) => Promise<void>;
  onFollowupAnswer?: (data: IOMainLLMMessage) => Promise<void>;
  onDocumentQuestion?: (data: IOMainLLMMessage) => Promise<void>;
  onDocumentAnswer?: (data: IOMainLLMMessage) => Promise<void>;
  onPlaybookQuestion?: (data: IOMainLLMMessage) => Promise<void>;
  onPlaybookAnswer?: (data: IOMainLLMMessage) => Promise<void>;
  onSummaryQuestion?: (data: IOMainLLMMessage) => Promise<void>;
  onSummaryAnswer?: (data: IOMainLLMMessage) => Promise<void>;
  onStatus?: (value: string) => void;
  onAppend?: (value: string) => void;
  onStart?: () => void;
  onError?: () => void;
  onClose?: () => void;
  onCaseCreated?: (data: v2CaseCreatedResponse) => void;
  workflowId?: string;
}

export const startLLMStreamV2 = async (llmStreamV2Params: LLMStreamV2Params): Promise<void> => {
  // maybe add timeout ?

  llmStreamV2Params.otherData ??= {};
  const requestData = !!llmStreamV2Params.llmStreamV2body
    ? JSON.stringify(llmStreamV2Params.llmStreamV2body)
    : !!llmStreamV2Params.llmStreamV2formData
      ? llmStreamV2Params.llmStreamV2formData
      : JSON.stringify(llmStreamV2Params.otherData);

  const llmRequestUrl = `${llmUrl}/v2/${llmStreamV2Params.endpointPath}`;
  const ssoToken = localStorage.getItem("sso_token");
  const token = ssoToken ? ssoToken : localStorage.getItem("authToken");

  await fetchEventSource(llmRequestUrl, {
    method: "POST",
    openWhenHidden: true,
    body: requestData,
    headers: {
      ...(llmStreamV2Params.llmStreamV2body ||
      requestData === JSON.stringify(llmStreamV2Params.otherData)
        ? { "Content-Type": "application/json" }
        : {}),
      Authorization: `Bearer ${token}`,
      Accept: "text/event-stream",
    },
    onopen: async (res: Response): Promise<void> => {
      if (res.ok && res.status === 200) {
        llmStreamV2Params.onStart?.();
      } else if (res.status >= 400 && res.status < 500 && res.status !== 429) {
        console.error("Client-Side Errror opening LLM Stream", res);
        llmStreamV2Params.onError?.();
      }
    },
    async onmessage(event: { data: string }) {
      if (!event.data || event.data.length == 0) {
        return;
      }
      const data: IMessageData = JSON.parse(event.data);
      const additionalData = {
        // server: serverName,
        model: llmStreamV2Params.model, // what to do in case of workflow?
        timestamp: Date.now(),
        workflowId: llmStreamV2Params.workflowId,
      };

      switch (data.type) {
        case "user_chat":
          llmStreamV2Params.onQuestion?.({
            ...data,
            category: "chat",
            role: "user",
            ...additionalData,
          });
          break;
        case "assistant_chat":
          llmStreamV2Params.onAnswer?.({
            ...data,
            category: "chat",
            role: "assistant",
            ...additionalData,
          });
          break;
        case "user_document":
          llmStreamV2Params.onDocumentQuestion?.({
            ...data,
            category: "document",
            role: "user",
            ...additionalData,
          });
          break;
        case "assistant_document":
          llmStreamV2Params.onDocumentAnswer?.({
            ...data,
            category: "document",
            role: "assistant",
            ...additionalData,
          });
          break;
        case "user_followup":
          llmStreamV2Params.onFollowupQuestion?.({
            ...data,
            category: "followup",
            role: "user",
            ...additionalData,
          });
          break;
        case "assistant_followup":
          llmStreamV2Params.onFollowupAnswer?.({
            ...data,
            category: "followup",
            role: "assistant",
            ...additionalData,
          });
          break;
        case "user_playbook":
          llmStreamV2Params.onPlaybookQuestion?.({
            ...data,
            category: "playbook",
            role: "user",
            ...additionalData,
          });
          break;
        case "assistant_playbook":
          llmStreamV2Params.onPlaybookAnswer?.({
            ...data,
            category: "playbook",
            role: "assistant",
            ...additionalData,
          });
          break;
        case "user_summary":
          llmStreamV2Params.onSummaryQuestion?.({
            ...data,
            category: "summary",
            role: "user",
            ...additionalData,
          });
          break;
        case "assistant_summary":
          llmStreamV2Params.onSummaryAnswer?.({
            ...data,
            category: "summary",
            role: "assistant",
            ...additionalData,
          });
          break;

        case "assistant_status":
          llmStreamV2Params.onStatus?.(data.content);
          break;

        case "heartbeat":
          break;
        case "case_created":
          llmStreamV2Params.onCaseCreated?.(data as v2CaseCreatedResponse);
          break;
        default:
          llmStreamV2Params.onAppend?.(data.content);
          break;
      }
    },
    onclose() {
      llmStreamV2Params.onClose?.();
    },
    onerror(err: any) {
      console.log("On error", err);
      llmStreamV2Params.onError?.();
      throw new Error();
    },
  });
};

// #############################################################################
// ############################# OTHER API FUNCTIONS ###########################
// #############################################################################
export interface CaseLLMWorkflowStatus {
  workflow_run_id: string;
  workflow_id: string;
  start_time: number;
  finish_time: number;
  processing_state: string;
}
export interface LlmCase {
  messages: LlmCaseMessage[];
  documents: CaseDocument[];
  llm_references: CaseLlmReference[] | null;
  llm_workflow_status: CaseLLMWorkflowStatus[] | null;
}
export interface CaseDocument {
  id: string;
  name: string;
  type: string;
  creation_date: number;
}
export interface CaseLlmReference {
  id: string;
  name: string;
  children: any[]; // ?
  reference_type: string;
}
export interface IMessageBase {
  content: string;
  temperature?: number;
  topic?: string;
  system_prompt?: string;
}
export interface IMessageMeta extends IMessageBase {
  model: string;
  timestamp: number;
}
export interface LlmCaseMessage extends IMessageBase {
  timestamp: number;
  role: LLMStreamEventType;
  model: string;
  prompt_id: string;
  topic: string;
  temperature: number;
  case_id: string;
  index_references: IndexReference[];
  system_prompt: string;
  workflow_run_id: string;
}
export interface IndexReference {
  id: string;
  score: number;
}
/**
 * This function retrieves a llm-case.
 *
 * @param caseId The id of the case
 *
 * @returns {Promise<JSON>} A promise with the case data as JSON
 */
export async function getLlmCase(caseId: string): Promise<LlmCase> {
  try {
    const response: AxiosResponse = await axios.get(`${apiUrl}/case/${caseId}`);
    return response.data as LlmCase;
  } catch (error) {
    console.error("Error trying to get llm-case:", error);
    throw new ce.CaseRetrievalError("Could not retrieve case. Please try again later.");
  }
}

export async function patchLlmCase(caseId: string, data: any): Promise<void> {
  try {
    const patchData = { id: caseId, type: "llm", ...data };
    const response: AxiosResponse = await axios.patch(`${apiUrl}/case/${caseId}`, patchData);
  } catch (error) {
    console.error("Error trying to patch llm-case:", error);
    throw new ce.CaseUpdateError("Could not update case. Please try again later.");
  }
}

export async function getLlmCaseDocumentId(caseId: string): Promise<string | null> {
  try {
    const llmCase: LlmCase = await getLlmCase(caseId);

    if (!llmCase.documents || llmCase.documents?.length === 0) return null;

    if (llmCase.documents.length === 1) return llmCase.documents[0].id;

    const prefixDocuments = llmCase.documents.filter((doc) =>
      doc.name.startsWith(documentNamePrefix)
    );
    if (prefixDocuments.length === 0) return null;

    if (prefixDocuments.length === 1) return prefixDocuments[0].id;

    // sort by creation_date -> first created = first in array
    prefixDocuments.sort((a, b) => a.creation_date - b.creation_date);
    return prefixDocuments[0].id;
  } catch (error) {
    console.error("Error trying to get llm-case document id:", error);
    throw new ce.CaseRetrievalError("Could not retrieve case document id. Please try again later.");
  }
}

export async function getCaseLlmReferences(caseId: string): Promise<CaseLlmReference[] | null> {
  try {
    const llmCase: LlmCase = await getLlmCase(caseId);
    return llmCase.llm_references;
  } catch (error) {
    console.error("Error trying to get llm-case references:", error);
    throw new ce.CaseRetrievalError("Could not retrieve case references. Please try again later.");
  }
}

export async function postDocumentLlmReference(
  caseId: string,
  files: documentFile[]
): Promise<void> {
  try {
    if (files.length === 0) return;

    const formData = new FormData();
    for (const file of files) {
      formData.append("files", file.originFileObj, file.name);
    }

    await axios.post(`${llmUrl}/v1/case/${caseId}/document`, formData);
  } catch (error) {
    console.error("Error trying to post document to case: ", error);
    throw new ce.DocumentUploadError("Could not upload document. Please try again later.");
  }
}

export async function deleteDocumentLlmReference(
  caseId: string,
  documentId: string
): Promise<void> {
  try {
    const requestUrl = `${apiUrl}/document/${documentId}`;
    try {
      const response = await axios.delete(requestUrl);
    } catch (error) {
      console.warn("Could not delete document, maybe already deleted: ", error);
    }

    const llmReferences = await getCaseLlmReferences(caseId);
    if (llmReferences !== null && llmReferences.length > 0) {
      const updatedReferences = llmReferences.filter((ref) => ref.id !== documentId);
      await patchLlmCase(caseId, { llm_references: updatedReferences });
    }
  } catch (error) {
    console.error("Error trying to delete document from case: ", error);
    throw new ce.DocumentDeletionError("Could not delete document. Please try again later.");
  }
}

type LlmModelDetailedRaw = {
  name: string;
  label: string;
  provider: string;
  max_context_window: number;
};
export type LlmModelDetailed = LlmModelDetailedRaw & {
  // id: string;
  // server: string;
  // serverDisplayName: string;
};
export let modelsDetailedDict: { [key: string]: LlmModelDetailed } = {};
export async function getLlmModelsDetailed(): Promise<LlmModelDetailed[]> {
  try {
    let modelsDetailed: LlmModelDetailed[] = [];

    const response: AxiosResponse = await axios.get(`${llmUrl}/v1/models/detailed`);
    const detailedModelRawArray: LlmModelDetailedRaw[] = response.data[
      "models"
    ] as LlmModelDetailedRaw[];

    modelsDetailed = modelsDetailed.concat(
      detailedModelRawArray.map(
        (detailedModelRaw) =>
          ({
            // base fields
            ...detailedModelRaw,
            // extra fields
            // id: `${serverName}:${detailedModelRaw.name}`,
            // server: serverName,
            // serverDisplayName: serverNameDict[serverName],
          }) as LlmModelDetailed
      )
    );

    modelsDetailed.forEach((model) => {
      modelsDetailedDict[model.name] = model;
    });
    return modelsDetailed;
  } catch (error) {
    console.error("Error trying to get llm-models:", error);
    throw new ce.LlmModelRetrievalError("Could not retrieve models. Please try again later.");
  }
}

export const getDefaultWorkflows = (): {
  [key in string]: string; // ? DEFAULT_WORKFLOWS
} => {
  if (process.env.NODE_ENV === "production") {
    return {
      anonymize: "043b44d9-d5e7-4561-bd4e-fbe25c9f5c2c",
      summary: "8b218651-d465-4ca6-a80d-ddcd639b1682",
      summary_100: "3de11722-225a-443e-9f78-a326cc37a1e6",
      summary_300: "76e0493f-cbcd-4e9f-9cfc-13d6e138cf0a",
      summary_500: "409622ac-7cd5-4209-9635-6413e49c009f",
    };
  }

  return {
    anonymize: "22b0f329-eea4-4632-b807-dc8fddf79b98",
    summary: "30ce991e-9fa8-4635-b934-e82c66e13f0c",
    summary_100: "27c1cd55-7a81-4a58-b8f3-298ad48352dd", // "40598bbe-5499-4ebf-bc59-ec5077dcd8b3",
    summary_300: "d7a713d8-1bc8-40c8-9d8e-c74b2078edb8", // "523d0b6d-9763-4687-b4fa-f3f32d81545c",
    summary_500: "315ebb93-f25e-4924-a582-a1d3fdc2513a", // "55969598-b13c-4278-84f7-2f1be03a7688",
  };
};

type CustomField = {
  label: string;
  value: string;
};
type CustomFieldMap = Record<string, CustomField>;
type WorkflowRaw = {
  id: string;
  tenant: number;
  title: string;
  description: string;
  prompts: WorkflowPrompt[] | string;
  parameters: null | {
    type: string[];
    custom: {} | CustomFieldMap;
  };
};
export type WorkflowPrompt = {
  id: string;
  data: WorkflowPromptData;
  children: WorkflowPrompt[];
};
export type WorkflowPromptData = {
  type: string;
  model: string;
  topic: string;
  content: WorkflowPromptContent[];
  // filename: string;
  // chunkNumber: number;
  // temperature: number;
  // system_prompt: string;
  // retrieval_prompt: string;
  // appendChunkNumber: number;
};
export type WorkflowPromptContent = {
  type: string;
  content: {
    type: string;
    text?: string;
    content?: WorkflowPromptContent;
  }[];
};
export type Workflow = {
  id: string;
  tenant: number;
  title: string;
  description: string;
  prompts: WorkflowPrompt[];
  modelName: string;
  // server: string;
  // serverDisplayName: string;
  // serverUrl: string;
  parameterTypes: string[];
  parameterCustoms: string[]; // keep it simple for now
};

async function getLlmWorkflows(): Promise<Workflow[]> {
  try {
    const response: AxiosResponse = await axios.get(`${apiUrl}/llm/workflow`, {
      timeout: 15000,
    });
    const workflows: WorkflowRaw[] = response.data as WorkflowRaw[];
    const mappedWorkflows: Workflow[] = [];
    workflows.forEach((workflow: WorkflowRaw) => {
      try {
        let parsedPrompts: WorkflowPrompt[] = [];
        if (typeof workflow.prompts === "string") {
          parsedPrompts = JSON.parse(workflow.prompts) as WorkflowPrompt[];
        } else {
          parsedPrompts = workflow.prompts as WorkflowPrompt[];
        }
        const modelName = parsedPrompts[0]["data"]["model"];
        const modelNameSplit = modelName.split("_");
        mappedWorkflows.push({
          id: workflow.id,
          tenant: workflow.tenant,
          title: workflow.title,
          description: workflow.description,
          prompts: parsedPrompts,
          modelName: modelNameSplit[1],
          // server: modelNameSplit[0],
          // serverDisplayName: serverNameDict[serverName],
          // serverUrl: serverUrlDict[modelNameSplit[0]], // llmUrl
          parameterTypes: workflow.parameters ? workflow.parameters.type : [],
          parameterCustoms: workflow.parameters
            ? workflow.parameters.custom
              ? Object.entries(workflow.parameters.custom)
                  .filter(([, val]) => val?.value === "true")
                  .map(([, val]) => val.label.toLowerCase())
              : []
            : [],
        });
      } catch (error) {
        console.warn(`Error parsing workflow: ${workflow.id}`, error);
      }
    });
    return mappedWorkflows;
  } catch (error: any) {
    if (error.code === "ECONNABORTED") {
      console.error("Timeout trying to get workflows");
    } else {
      console.error("Error trying to get workflows:", error);
    }
    return Promise.reject(error);
  }
}

// #############################################################################
// ############################# DOCUMENT ######################################
// #############################################################################

export async function updateDocumentContent(documentId: string, text: string) {
  try {
    const response: AxiosResponse = await axios.put(
      `${apiUrl}/document/${documentId}/annotations`,
      { text: text, meta: {}, annotations: [] },
      { timeout: 10000 }
    );
    return response.data;
  } catch (error) {
    console.error("Error trying to update document content:", error);
    throw new ce.DocumentUpdateError("Could not update document content. Please try again later.");
  }
}

// #############################################################################
// ############################# PLAYBOOK ######################################
// #############################################################################

export const playbookIdDict: { [key: string]: LLMPlaybook } = {};
export type ServerType = "premium" | "standard" | "hetzner";
interface PlaybookDocument {
  name: string;
  id: string;
}
interface LLMPlaybookRaw {
  id: string;
  title: string;
  owner: string;
  tenant: string;
  index_location: ServerType;
  creation_date: number;
  documents: PlaybookDocument[];
}
export interface LLMPlaybook extends LLMPlaybookRaw {
  // server: string;
  // serverDisplayName: string;
  // serverUrl: string;
}
export async function getLlmPlaybooks(): Promise<LLMPlaybook[]> {
  try {
    const response: AxiosResponse = await axios.get(`${apiUrl}/llm/playbook`, {
      timeout: 15000,
    });
    let playbooksRaw: LLMPlaybookRaw[] = response.data as LLMPlaybookRaw[];
    playbooksRaw = playbooksRaw.filter((playbook) => playbook.index_location !== "hetzner");

    // const serverUrlDict = await getLLMUrls();
    const playbooks = playbooksRaw.map((playbook) => {
      return {
        ...playbook,
        // server: playbook.index_location,
        // serverDisplayName: serverNameDict[playbook.index_location],
        // serverUrl: serverUrlDict[playbook.index_location],
      };
    });
    playbooks.forEach((playbook) => {
      playbookIdDict[playbook.id] = playbook;
    });
    return playbooks;
  } catch (error: any) {
    if (error.code === "ECONNABORTED") {
      console.error("Timeout trying to get playbooks");
    } else {
      console.error("Error trying to get playbooks:", error);
    }
    return Promise.reject(error);
  }
}

export interface PlaybookDocumentContent {
  text: string;
}
export async function getPlaybookDocumentContent(
  documentId: string
): Promise<PlaybookDocumentContent> {
  try {
    const response: AxiosResponse = await axios.get(`${apiUrl}/document/${documentId}/annotations`);
    return response.data as PlaybookDocumentContent;
  } catch (error: any) {
    if (error.code === "ECONNABORTED") {
      console.error("Timeout trying to get document content of playbook");
    } else {
      console.error("Error trying to get document content of playbook:", error);
    }
    throw new ce.PlaybookDocumentLoadError(
      "Could not retrieve document content of playbook. Please try again later."
    );
  }
}

export interface CreatePlaybookDTO {
  title: string;
  description: string;
}
export interface CreatePlaybookResponse {
  id: string;
  title: string;
  description: string;
  tenant: string;
  owner: string;
  documents: [] | null;
  index_location: string;
  creation_date: number;
  last_modified: number | null;
}
export async function createPlaybook(payload: CreatePlaybookDTO): Promise<CreatePlaybookResponse> {
  try {
    const response = await axios.post(`${llmUrl}/v2/index/playbook`, payload);
    return response.data as CreatePlaybookResponse;
  } catch (err) {
    throw err; // better error handling?
  }
}

export async function addDocumentToPlaybook(
  playbookId: string,
  file: documentFile
): Promise<string> {
  try {
    const formData = new FormData();
    formData.append("file", file.originFileObj, file.name);
    const response = await axios.post(`${llmUrl}/v2/index/playbook/${playbookId}`, formData);
    if (response.status === 200) return "OK";
    throw new Error("Error adding document to playbook");
  } catch (err) {
    throw err; // better error handling?
  }
}

// #############################################################################
// ############################# LLM V1 API FUNCTIONS ##########################
// #############################################################################

export type LLMStreamEventCategory = "chat" | "document" | "playbook" | "followup" | "summary";
export type LLMStreamEventRole = "user" | "assistant";
export type LLMStreamEventType =
  | "user_chat"
  | "assistant_chat"
  | "user_document"
  | "assistant_document"
  | "user_playbook"
  | "assistant_playbook"
  | "user_followup"
  | "assistant_followup"
  | "sources"
  | "assistant_status"
  | "user_summary"
  | "assistant_summary"
  | "heartbeat"
  | "case_created"
  | "index_created";
export function isUserRole(type: LLMStreamEventType | string): boolean {
  return type.startsWith("user_");
}

export const LLMStreamEventLabel: { [key in LLMStreamEventType]: string } = {
  user_chat: "Frage", //"Q",
  assistant_chat: "Antwort",
  user_document: "Frage", //"Q",
  assistant_document: "Antwort",
  user_playbook: "Abgleich mit Playbook Prompt", //"Abgleich mit Playbook Prompt",
  assistant_playbook: "Abgleich mit Playbook", //"Abgleich mit Playbook",
  user_followup: "Follow-up Prompt", //"Follow-up Prompt",
  assistant_followup: "Follow-up Antwort", //" Follow-up Antwort",
  sources: "",
  assistant_status: "Status",
  user_summary: "Frage (Zusammenfassung)", //"Q",
  assistant_summary: "Zusammenfassung",
  heartbeat: "",
  case_created: "",
  index_created: "",
};
export const LLMTypeToRoleAndCategory: {
  [key in LLMStreamEventType]: {
    role: LLMStreamEventRole;
    category: LLMStreamEventCategory;
  };
} = {
  user_chat: { role: "user", category: "chat" },
  assistant_chat: { role: "assistant", category: "chat" },
  user_document: { role: "user", category: "document" },
  assistant_document: { role: "assistant", category: "document" },
  user_playbook: { role: "user", category: "playbook" },
  assistant_playbook: { role: "assistant", category: "playbook" },
  user_followup: { role: "user", category: "followup" },
  assistant_followup: { role: "assistant", category: "followup" },
  user_summary: { role: "user", category: "summary" },
  assistant_summary: { role: "assistant", category: "summary" },
  assistant_status: { role: "assistant", category: "chat" },
  sources: { role: "assistant", category: "chat" },
  heartbeat: { role: "assistant", category: "chat" },
  case_created: { role: "assistant", category: "chat" },
  index_created: { role: "assistant", category: "chat" },
};
export const LLMMessageCategoryToDisplayType: { [key: string]: string } = {
  chat: "Chat",
  document: "Chat mit Dokument",
  playbook: "Chat mit Playbook",
  followup: "Chat mit Playbook",
  summary: "Zusammenfassung",
};

export type LLMStreamType =
  | "chat/completions"
  | "rag"
  | "workflow"
  | "workflow/remote"
  | "workflow/legacy"
  | "rag/summary";
export interface IMessageData extends IMessageBase {
  type: LLMStreamEventType;
}
export interface IMessageDataPlus extends IMessageData {
  category: LLMStreamEventCategory;
  role: LLMStreamEventRole;
}

export interface IOMainLLMMessage extends IMessageDataPlus {
  // server: string;
  model: string;
  timestamp: number;
  retrieval_prompt?: string; // fix messy interfaces
  isHistoryLoading?: boolean;
  topicIsActive?: boolean;
  workflowId?: string;
}

export type LLMStreamEventRolePlus = LLMStreamEventRole | "option";
export interface IOBasicMessage {
  text: string;
  role: LLMStreamEventRolePlus;
}

// #############################################################################
// ############################# AUDIO #########################################
// #############################################################################

export async function postAudioRecording(audioFile: File): Promise<string> {
  try {
    const formData = new FormData();
    formData.append("file", audioFile);
    formData.append("diarize", "false"); // check if needed

    let lang = await getSetting("language");
    if (lang !== undefined && lang !== null) {
      lang = lang as string;
    } else {
      lang = getCurrentLanguage(); // check language format
    }
    if (!["de", "en"].includes(lang)) lang = "de"; // just in case

    formData.append("language", lang);

    const environment = process.env.NODE_ENV === "production" ? "production" : "stage";
    const response = await axios.post(
      `${llmUrl}/audio/transcribe?environment=${environment}`,
      formData
    );
    if (response.status === 200) {
      return response.data as string;
    }
    throw new Error("Error transcribing audio");
  } catch (err) {
    throw err; // better error handling?
  }
}
